import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  ChevronDown,
  ChevronRight,
  Folder,
  Loader2,
  MoreHorizontal,
  Plus,
} from 'lucide-react'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import type { LabelDocument } from '@/lib/db/rxdb/schemas/label'
import type { LabelGroupDocument } from '@/lib/db/rxdb/schemas/labelGroup'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { usePageLayout } from '@/hooks/usePageLayout'
import SettingsSidebar from '@/components/app/sidebars/settings'
import { SettingsPageContainer, SettingsPageHeader } from '@/components/app/layout'
import useLoginStore from '@/store/LoginStore'
import { useAllLabels } from '@/lib/hooks/useLabels'
import { useAllLabelGroups } from '@/lib/hooks/useLabelGroups'
import { useProjects } from '@/routes/features/tasks/hooks/useProjects'
import {
  CreateLabelDialog,
  CreateLabelGroupDialog,
} from '@/components/features/labels/components'

type ScopeFilter = 'all' | 'workspace' | 'project'

interface LabelTableRow {
  id: string
  type: 'label' | 'group'
  name: string
  description: string | null
  color?: string
  projectId: string | null
  labelGroupId?: string | null
  createdOn?: number
  updatedOn?: number
  labelRecord?: LabelDocument
  groupRecord?: LabelGroupDocument
  matchesSearch: boolean
}

export default function LabelSettingsPage() {
  const { t } = useTranslation('settings')
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const workspaceId = useLoginStore((state) => state.workspaceId)
  const [search, setSearch] = useState('')
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all')
  const [projectFilter, setProjectFilter] = useState<string>('all')
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(
    () => new Set(),
  )
  const [labelDialogOpen, setLabelDialogOpen] = useState(false)
  const [labelDialogMode, setLabelDialogMode] = useState<'create' | 'edit'>(
    'create',
  )
  const [activeLabel, setActiveLabel] = useState<LabelDocument | null>(null)
  const [labelDialogProjectId, setLabelDialogProjectId] = useState<
    string | null
  >(null)

  const [groupDialogOpen, setGroupDialogOpen] = useState(false)
  const [groupDialogMode, setGroupDialogMode] = useState<'create' | 'edit'>(
    'create',
  )
  const [activeGroup, setActiveGroup] = useState<LabelGroupDocument | null>(
    null,
  )
  const [groupDialogProjectId, setGroupDialogProjectId] = useState<
    string | null
  >(null)

  const { labels, isLoading: labelsLoading } = useAllLabels(workspaceId)
  const { labelGroups, isLoading: labelGroupsLoading } = useAllLabelGroups(
    workspaceId,
  )
  const { projects } = useProjects()
  const projectMap = useMemo(
    () => new Map(projects.map((project) => [project.id, project])),
    [projects],
  )

  useEffect(() => {
    if (scopeFilter !== 'project' && projectFilter !== 'all') {
      setProjectFilter('all')
    }
  }, [scopeFilter, projectFilter])

  useEffect(() => {
    if (
      scopeFilter === 'project' &&
      projectFilter === 'all' &&
      projects[0]
    ) {
      setProjectFilter(projects[0].id)
    }
  }, [scopeFilter, projectFilter, projects])

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }, [])

  const normalizeText = (value?: string | null) =>
    value?.toLowerCase() ?? ''

  const scopePredicate = useCallback(
    (itemProjectId: string | null) => {
      if (scopeFilter === 'workspace') {
        return !itemProjectId
      }
      if (scopeFilter === 'project') {
        if (projectFilter === 'all') {
          return true
        }
        return itemProjectId === projectFilter
      }
      return true
    },
    [projectFilter, scopeFilter],
  )

  const searchTerm = normalizeText(search.trim())
  const isSearching = Boolean(searchTerm)

  const { smartTime, dateTime: formatDateTime } = useDateTimeFormatters()
  const formatTimestamp = useCallback((value?: number) => {
    if (!value) {
      return { display: '—', title: undefined, iso: undefined }
    }

    try {
      const date = new Date(value)
      return {
        display: smartTime(date),
        title: formatDateTime(date, { dateStyle: 'medium', timeStyle: 'short' }),
        iso: date.toISOString(),
      }
    } catch {
      return { display: '—', title: undefined, iso: undefined }
    }
  }, [smartTime, formatDateTime])

  const tableRows = useMemo(() => {
    const labelRows: Array<LabelTableRow> = labels
      .filter((label) => scopePredicate(label.projectId ?? null))
      .map((label) => {
        const matchesName = normalizeText(label.name).includes(searchTerm)
        const matchesDescription = normalizeText(
          label.description ?? '',
        ).includes(searchTerm)

        return {
          id: label.id,
          type: 'label' as const,
          name: label.name,
          description: label.description ?? '',
          color: label.color,
          projectId: label.projectId ?? null,
          labelGroupId: label.labelGroupId ?? null,
          createdOn: label.createdOn,
          updatedOn: label.updatedOn,
          labelRecord: label,
          matchesSearch: !isSearching || matchesName || matchesDescription,
        }
      })

    const labelsByGroup = new Map<string, Array<LabelTableRow>>()
    labelRows.forEach((row) => {
      if (!row.labelGroupId) {
        return
      }
      const existing = labelsByGroup.get(row.labelGroupId) ?? []
      existing.push(row)
      labelsByGroup.set(row.labelGroupId, existing)
    })

    const groupRows: Array<LabelTableRow> = labelGroups
      .filter((group) => scopePredicate(group.projectId ?? null))
      .map((group) => {
        const matches =
          normalizeText(group.name).includes(searchTerm) ||
          normalizeText(group.description ?? '').includes(searchTerm)

        return {
          id: group.id,
          type: 'group' as const,
          name: group.name,
          description: group.description ?? '',
          color: undefined,
          projectId: group.projectId ?? null,
          labelGroupId: undefined,
          createdOn: group.createdOn,
          updatedOn: group.updatedOn,
          groupRecord: group,
          matchesSearch: !isSearching || matches,
        }
      })

    const orderedRows: Array<LabelTableRow> = []
    const sortedGroupRows = groupRows.sort((a, b) =>
      a.name.localeCompare(b.name),
    )

    sortedGroupRows.forEach((groupRow) => {
      const children = labelsByGroup.get(groupRow.id)?.sort((a, b) =>
        a.name.localeCompare(b.name),
      ) ?? []
      const childMatches = isSearching
        ? children.filter((child) => child.matchesSearch)
        : children

      const shouldRenderGroup =
        groupRow.matchesSearch || childMatches.length > 0

      if (!shouldRenderGroup) {
        return
      }

      orderedRows.push(groupRow)
      const isExpanded =
        isSearching || expandedGroupIds.has(groupRow.id)

      if (isExpanded) {
        orderedRows.push(...childMatches)
      }
    })

    const ungroupedLabels = labelRows
      .filter((row) => !row.labelGroupId && row.matchesSearch)
      .sort((a, b) => a.name.localeCompare(b.name))

    orderedRows.push(...ungroupedLabels)

    return orderedRows
  }, [
    expandedGroupIds,
    isSearching,
    labelGroups,
    labels,
    scopePredicate,
    searchTerm,
  ])

  const handleEditLabel = useCallback((label: LabelDocument) => {
    setActiveLabel(label)
    setLabelDialogProjectId(label.projectId ?? null)
    setLabelDialogMode('edit')
    setLabelDialogOpen(true)
  }, [])

  const handleEditGroup = useCallback((group: LabelGroupDocument) => {
    setActiveGroup(group)
    setGroupDialogProjectId(group.projectId ?? null)
    setGroupDialogMode('edit')
    setGroupDialogOpen(true)
  }, [])

  const columns = useMemo<Array<ColumnDef<LabelTableRow>>>(() => {
    return [
      {
        accessorKey: 'name',
        header: () => <span>Name</span>,
        enableSorting: false,
        cell: ({ row }) => {
          const item = row.original
          const isGroup = item.type === 'group'
          return (
            <div
              className={cn(
                'flex items-center gap-2',
                !isGroup && item.labelGroupId && 'pl-8',
              )}
            >
              {isGroup ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-6 text-muted-foreground"
                  onClick={() => toggleGroup(item.id)}
                >
                  {expandedGroupIds.has(item.id) ? (
                    <ChevronDown className="size-4" />
                  ) : (
                    <ChevronRight className="size-4" />
                  )}
                </Button>
              ) : (
                <span className="size-6" />
              )}
              {isGroup ? (
                <Folder className="size-4 text-muted-foreground" />
              ) : (
                <span
                  className="size-3 rounded-full border"
                  style={{ backgroundColor: item.color }}
                />
              )}
              <span className="font-medium text-sm text-foreground">
                {item.name}
              </span>
            </div>
          )
        },
      },
      {
        accessorKey: 'description',
        header: () => <span>Description</span>,
        cell: ({ row }) => {
          const description = row.original.description
          return description ? (
            <span className="text-sm text-muted-foreground">{description}</span>
          ) : (
            <span className="text-sm text-muted-foreground/60">
              Add label description...
            </span>
          )
        },
      },
      {
        accessorKey: 'projectId',
        header: () => <span>Scope</span>,
        cell: ({ row }) => {
          const projectId = row.original.projectId
          if (!projectId) {
            return <span className="text-sm text-muted-foreground">Workspace</span>
          }
          return (
            <span className="text-sm text-muted-foreground">
              {projectMap.get(projectId)?.name ?? 'Project'}
            </span>
          )
        },
      },
      {
        id: 'lastUpdated',
        accessorFn: (row) => row.updatedOn ?? 0,
        header: () => <span>Last Updated</span>,
        cell: ({ row }) => {
          const t = formatTimestamp(row.original.updatedOn)
          return (
            <time
              className="text-sm text-muted-foreground"
              title={t.title}
              dateTime={t.iso}
            >
              {t.display}
            </time>
          )
        },
      },
      {
        id: 'createdOn',
        accessorFn: (row) => row.createdOn ?? 0,
        header: () => <span>Created</span>,
        cell: ({ row }) => {
          const t = formatTimestamp(row.original.createdOn)
          return (
            <time
              className="text-sm text-muted-foreground"
              title={t.title}
              dateTime={t.iso}
            >
              {t.display}
            </time>
          )
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground"
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  if (row.original.type === 'group' && row.original.groupRecord) {
                    handleEditGroup(row.original.groupRecord)
                  } else if (row.original.labelRecord) {
                    handleEditLabel(row.original.labelRecord)
                  }
                }}
              >
                Edit {row.original.type === 'group' ? 'group' : 'label'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ]
  }, [
    expandedGroupIds,
    formatTimestamp,
    handleEditGroup,
    handleEditLabel,
    projectMap,
    toggleGroup,
  ])

  const isBusy = labelsLoading || labelGroupsLoading

  const handleNewLabel = () => {
    setLabelDialogMode('create')
    setActiveLabel(null)
    setLabelDialogProjectId(
      scopeFilter === 'project' && projectFilter !== 'all'
        ? projectFilter
        : null,
    )
    setLabelDialogOpen(true)
  }

  const handleNewGroup = () => {
    setGroupDialogMode('create')
    setActiveGroup(null)
    setGroupDialogProjectId(
      scopeFilter === 'project' && projectFilter !== 'all'
        ? projectFilter
        : null,
    )
    setGroupDialogOpen(true)
  }

  return (
    <SettingsPageContainer innerClassName="space-y-6">
        <SettingsPageHeader
          title={t('labels.title')}
          action={
            <>
              <Button variant="outline" onClick={handleNewGroup}>
                <Plus className="size-4" />
                {t('labels.newGroup')}
              </Button>
              <Button onClick={handleNewLabel}>
                <Plus className="size-4" />
                {t('labels.newLabel')}
              </Button>
            </>
          }
        />

        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full max-w-xs">
            <Input
              placeholder={t('labels.filterPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-9"
            />
          </div>
          <Select
            value={scopeFilter}
            onValueChange={(value: ScopeFilter) => setScopeFilter(value)}
          >
            <SelectTrigger className="w-40 h-9">
              <SelectValue placeholder={t('labels.scopePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('labels.scopeAll')}</SelectItem>
              <SelectItem value="workspace">{t('labels.scopeWorkspace')}</SelectItem>
              <SelectItem value="project">{t('labels.scopeProject')}</SelectItem>
            </SelectContent>
          </Select>
          {scopeFilter === 'project' && (
            <Select
              value={projectFilter}
              onValueChange={setProjectFilter}
              disabled={projects.length === 0}
            >
              <SelectTrigger className="w-48 h-9">
                <SelectValue placeholder={t('labels.chooseProject')} />
              </SelectTrigger>
              <SelectContent>
                {projects.length === 0 ? (
                  <SelectItem value="all" disabled>
                    {t('labels.noProjectsFound')}
                  </SelectItem>
                ) : (
                  projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          )}
        </div>

        {isBusy ? (
          <div className="flex h-64 items-center justify-center rounded-md border bg-card">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <LabelsDataTable columns={columns} data={tableRows} />
        )}

      <CreateLabelDialog
        open={labelDialogOpen}
        onOpenChange={setLabelDialogOpen}
        mode={labelDialogMode}
        initialLabel={activeLabel}
        projectId={labelDialogProjectId}
        projects={projects}
        labelGroups={labelGroups}
        existingLabels={labels}
      />

      <CreateLabelGroupDialog
        open={groupDialogOpen}
        onOpenChange={setGroupDialogOpen}
        mode={groupDialogMode}
        initialGroup={activeGroup}
        projectId={groupDialogProjectId}
        projects={projects}
      />
    </SettingsPageContainer>
  )
}

function LabelsDataTable({
  columns,
  data,
}: {
  columns: Array<ColumnDef<LabelTableRow>>
  data: Array<LabelTableRow>
}) {
  const [sorting, setSorting] = useState<SortingState>([])

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    enableSortingRemoval: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="rounded-md border bg-card">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-32 text-center text-muted-foreground"
              >
                No labels found for this filter.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/labels',
)({
  component: LabelSettingsPage,
})
